import {
  Box,
  Flex,
  Text
} from '@chakra-ui/react';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button } from '@morressier/alembic';
import { IAffiliationDetails, IJournalWiseUserRoleList } from '@publishing-engine/domain/models';
import { MergeCountLabels } from './MergeAccountsPanel';
import Footer from 'src/components/Footer';
import NavbarDashboard from 'src/components/NavbarDashboard';
import UserItem from '../../SubmissionContexts/Users/UserItem';
import UserTagBadgeList, { MergeUserTagSummary } from './UserTagBadgeList';

interface MergeAccountsSuccessStateProps {
  mergedCount: number;
  mergedProfile: {
    userId: string;
    fullName: string;
    email: string;
    submissions: string;
    draftSubmissions: string;
    archiveSubmissions: string;
    inProgressReviews: string;
    completedReviews: string;
    invitedReviews: string;
    totalReviews: string;
    orcid: string;
    role: string;
    roles: string[];
    journals: IJournalWiseUserRoleList[];
    tags: MergeUserTagSummary[];
    affiliation: string;
    affiliationDetails?: IAffiliationDetails;
  };
  countLabels: MergeCountLabels;
  onBackToUserList: () => void;
  onViewMergedProfile: () => void;
}

const MergeAccountsSuccessState = ({
  mergedProfile,
  countLabels,
  onBackToUserList,
  onViewMergedProfile
}: MergeAccountsSuccessStateProps) => {
  return (
    <Flex
      flexDirection="column"
      backgroundColor="surfacePrimaryFill"
      minH="100vh"
    >
      <NavbarDashboard />

      <Flex
        flex="1"
        direction="column"
        alignItems="center"
        justifyContent="center"
        paddingX={4}
        paddingY={8}
      >
        <Flex
          direction="column"
          alignItems="center"
          width="100%"
          maxWidth="900px"
          gap={8}
        >
          <Flex
            height="84px"
            width="100%"
            background="#B6F5A8"
            justifyContent="center"
            alignItems="center"
            borderRadius="base"
            color="#2E7D32"
          >
            <FontAwesomeIcon fontSize={40} icon={faCircleCheck} />
          </Flex>

          <Flex
            direction="column"
            alignItems="center"
            gap={2}
            textAlign="center"
          >
            <Text textStyle="headingTitle">Accounts merged successfully</Text>
            <Text textStyle="textBody">
              The selected accounts have been unified into a single profile. All
              roles, submission history, and review records have been
              consolidated.
            </Text>
          </Flex>

          <Box
            width="100%"
            maxWidth="900px"
            border="1px solid"
            borderColor="#EAECF0"
            borderRadius="md"
            backgroundColor="surfacePrimaryFill"
            boxShadow="sm"
            padding={5}
          >
            <Flex direction="column" gap={4}>
              <Flex justifyContent="flex-start">
                <Badge size="context" variant="neutral">
                  Merged Profile
                </Badge>
              </Flex>

              <Flex direction="column" gap={1}>
                <Text textStyle="headingGroup">{mergedProfile.fullName}</Text>
                <Text textStyle="textBody">{mergedProfile.email}</Text>
              </Flex>

              <Flex direction="column" gap={3}>
                <Flex direction="column" gap={1}>
                  <Text textStyle="headingItem">ORCiD</Text>
                  <Text textStyle="textBody">{mergedProfile.orcid}</Text>
                </Flex>

                <Flex direction="column" gap={1}>
                  <Text textStyle="headingItem">Affiliation</Text>
                  {mergedProfile.affiliationDetails ? (
                    <Flex direction="column" gap={1}>
                      <Text textStyle="headingItem">
                        {mergedProfile.affiliationDetails.organizationName}
                      </Text>
                      {(mergedProfile.affiliationDetails.country ||
                        mergedProfile.affiliationDetails.countryCode) && (
                        <Text textStyle="textBody">
                          {mergedProfile.affiliationDetails.country}
                          {mergedProfile.affiliationDetails.countryCode &&
                            ` (${mergedProfile.affiliationDetails.countryCode})`}
                        </Text>
                      )}
                      {mergedProfile.affiliationDetails.streetAddress && (
                        <Text textStyle="textBody">
                          {mergedProfile.affiliationDetails.streetAddress}
                        </Text>
                      )}
                      {(mergedProfile.affiliationDetails.city ||
                        mergedProfile.affiliationDetails.postalCode) && (
                        <Text textStyle="textBody">
                          {[
                            mergedProfile.affiliationDetails.city,
                            mergedProfile.affiliationDetails.postalCode
                          ]
                            .filter(Boolean)
                            .join('- ')}
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    <Text textStyle="textBody" whiteSpace="pre-wrap">
                      {mergedProfile.affiliation}
                    </Text>
                  )}
                </Flex>

                <Flex direction="column" gap={1}>
                  <Text textStyle="headingItem">Journal & Roles</Text>
                  <UserItem data={mergedProfile.journals} />
                </Flex>

                <Flex direction="column" gap={1}>
                  <Text textStyle="headingItem">Tags</Text>
                  <UserTagBadgeList
                    tags={mergedProfile.tags}
                    keyPrefix="merge-success-tags"
                  />
                </Flex>
              </Flex>

              <Flex direction={{ base: 'column', md: 'row' }} alignItems="stretch" gap={0}>
                <Flex direction="column" gap={1} flex="1" pr={{ base: 0, md: 4 }}>
                  <Text textStyle="headingItem">Submissions</Text>
                  <Flex gap={2} wrap="wrap">
                    {[
                      { label: countLabels.activeSubmissions, value: mergedProfile.submissions },
                      { label: countLabels.draftSubmissions, value: mergedProfile.draftSubmissions },
                      { label: countLabels.archiveSubmissions, value: mergedProfile.archiveSubmissions }
                    ].map(({ label, value }) => (
                      <Badge key={label} size="context" variant="neutral">
                        {label}: {value}
                      </Badge>
                    ))}
                  </Flex>
                </Flex>
                <Box display={{ base: 'none', md: 'block' }} width="1px" backgroundColor="#D9D9D9" alignSelf="stretch" />
                <Flex direction="column" gap={1} flex="1" pl={{ base: 0, md: 4 }} pt={{ base: 3, md: 0 }}>
                  <Text textStyle="headingItem">Reviews</Text>
                  <Flex gap={2} wrap="wrap">
                    {[
                      { label: countLabels.totalReviews, value: mergedProfile.totalReviews },
                      { label: countLabels.inProgressReviews, value: mergedProfile.inProgressReviews },
                      { label: countLabels.completedReviews, value: mergedProfile.completedReviews },
                      { label: countLabels.invitedReviews, value: mergedProfile.invitedReviews }
                    ].map(({ label, value }) => (
                      <Badge key={label} size="context" variant="neutral">
                        {label}: {value}
                      </Badge>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Box>

          <Box width="100%" maxWidth="900px">
            <Flex
              justifyContent="flex-start"
              wrap="wrap"
              direction={{ base: 'column', md: 'row' }}
              gap={5}
            >
              <Button
                variant="secondary"
                onClick={onBackToUserList}
                width={{ base: '100%', md: 'auto' }}
              >
                Back to User List
              </Button>
              <Button
                variant="primary"
                onClick={onViewMergedProfile}
                width={{ base: '100%', md: 'auto' }}
              >
                View Merged Profile
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Flex>

      <Footer />
    </Flex>
  );
};

export default MergeAccountsSuccessState;
